import { Button } from '@material-ui/core';
import { EditorRow } from 'components/common/styles';
import React, { useContext, useMemo, useState } from 'react';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer';
import styled from 'styled-components';
import { ModalWrapper } from '../../../components/ModalWrapper';
import FileUpload from '../../../CMFW/input/FileUpload';
import ModuleContext from '../../../CMFW/context/ModuleContext';
import MemoryPhrasebookModel, {
  IPhrasebookImportDiff,
  IPhrasebookImportExecuteEntry,
  IPhrasebookImportExecutePayload,
} from '../../../models/MemoryPhrasebookModel';

type IProps = {
  onClose: () => void;
  onImportComplete: () => void;
};

type Step = 'select' | 'review' | 'done';

interface IGroupedDiff {
  translationDialectId: number;
  dialect: string;
  phrasebookId: number;
  textDiff?: { oldValue: string; newValue: string };
  statusDiff?: { oldValue: string; newValue: string };
}

const DIFF_STYLES = {
  wordDiff: { display: 'inline' },
  diffRemoved: { display: 'none' },
  diffAdded: { display: 'none' },
  content: { display: 'block!important', fontSize: 12 },
  diffContainer: { overflow: 'hidden' },
  line: { wordBreak: 'break-word' as const },
};

const MemoryPhrasebookImportModal: React.FC<IProps> = (props) => {
  const [step, setStep] = useState<Step>('select');
  const [diffs, setDiffs] = useState<IPhrasebookImportDiff[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const moduleModel = useContext(ModuleContext) as MemoryPhrasebookModel;

  const handleFileChosen = (selectedFile: File) => {
    setFile(selectedFile);
  };

  const handleFileUpload = async () => {
    if (!file) return;

    setLoading(true);
    setErrors([]);
    const response = await moduleModel.validateImportCSV(file);
    setLoading(false);

    if (response.errors && response.errors.length > 0) {
      setErrors(response.errors);
      return;
    }

    setDiffs(response.diffs);
    setStep('review');
  };

  const handleSubmit = async () => {
    setLoading(true);

    const payload: IPhrasebookImportExecutePayload = { notes: [], dialects: [] };

    const notesDiffs = diffs.filter((d) => d.type === 'notes');
    notesDiffs.forEach((diff) => {
      payload.notes.push({ phrasebookId: diff.phrasebookId, newValue: diff.newValue });
    });

    const dialectDiffs = diffs.filter((d) => d.type === 'dialect');
    const grouped: Record<number, IPhrasebookImportExecuteEntry> = {};
    dialectDiffs.forEach((diff) => {
      if (!diff.translationDialectId) return;
      if (!grouped[diff.translationDialectId]) {
        grouped[diff.translationDialectId] = { translationDialectId: diff.translationDialectId };
      }
      if (diff.field === 'text') {
        grouped[diff.translationDialectId].text = diff.newValue;
      }
      if (diff.field === 'status') {
        grouped[diff.translationDialectId].status = diff.newValue;
      }
    });
    Object.values(grouped).forEach((entry) => payload.dialects.push(entry));

    const response = await moduleModel.executeImport(payload);
    setLoading(false);

    if (response.errors && response.errors.length > 0) {
      setErrors(response.errors);
    } else {
      setResultMessage(`Successfully updated ${response.updated} entries.`);
      setStep('done');
      props.onImportComplete();
    }
  };

  const { notesDiffs, groupedByDialect } = useMemo(() => {
    const notes = diffs.filter((d) => d.type === 'notes');
    const dialectDiffs = diffs.filter((d) => d.type === 'dialect');

    const entryMap: Record<number, IGroupedDiff> = {};
    dialectDiffs.forEach((diff) => {
      if (!diff.translationDialectId) return;
      if (!entryMap[diff.translationDialectId]) {
        entryMap[diff.translationDialectId] = {
          translationDialectId: diff.translationDialectId,
          dialect: diff.dialect || 'Unknown',
          phrasebookId: diff.phrasebookId,
        };
      }
      if (diff.field === 'text') {
        entryMap[diff.translationDialectId].textDiff = { oldValue: diff.oldValue, newValue: diff.newValue };
      }
      if (diff.field === 'status') {
        entryMap[diff.translationDialectId].statusDiff = { oldValue: diff.oldValue, newValue: diff.newValue };
      }
    });

    const byDialect: Record<string, IGroupedDiff[]> = {};
    Object.values(entryMap).forEach((entry) => {
      if (!byDialect[entry.dialect]) {
        byDialect[entry.dialect] = [];
      }
      byDialect[entry.dialect].push(entry);
    });

    return { notesDiffs: notes, groupedByDialect: byDialect };
  }, [diffs]);

  const getHeight = () => {
    if (step === 'select') return 320;
    if (step === 'review') return 500;
    return 150;
  };

  return (
    <ModalWrapper
      onClose={props.onClose}
      title={'Import Phrasebook CSV'}
      width={step === 'review' ? 700 : 400}
      height={getHeight()}
    >
      {step === 'select' && (
        <>
          <InfoSection>
            <InfoTitle>CSV Format</InfoTitle>
            <InfoText>
              <code>phrasebookId,translationID,notes,UK_text,UK_status,US_text,US_status,...</code>
            </InfoText>
            <InfoTitle>Valid status values</InfoTitle>
            <InfoText>Pending, Done</InfoText>
            <InfoText>Entries with Legacy or From Target status cannot be updated.</InfoText>
          </InfoSection>
          <EditorRowStyled>
            <span>Select CSV file: </span>
            <FileUploadStyled handleFile={handleFileChosen} />
            {file && <FileName>{file.name}</FileName>}
          </EditorRowStyled>
          {errors.length > 0 && (
            <ErrorSection>
              {errors.map((err, i) => (
                <div key={i}>{err}</div>
              ))}
            </ErrorSection>
          )}
          <EditorRowStyled>
            <Button variant="contained" size="small" disabled={!file || loading} onClick={handleFileUpload}>
              {loading ? 'Validating...' : 'Validate'}
            </Button>
          </EditorRowStyled>
        </>
      )}

      {step === 'review' && (
        <>
          {diffs.length === 0 ? (
            <Section>No changes detected.</Section>
          ) : (
            <DiffScrollContainer>
              {notesDiffs.length > 0 && (
                <DialectSection>
                  <DialectLabel>Notes</DialectLabel>
                  {notesDiffs.map((diff) => (
                    <DiffEntry key={`notes-${diff.phrasebookId}`}>
                      <EntryHeader>Phrasebook ID: {diff.phrasebookId}</EntryHeader>
                      <EntryContent>
                        <DiffField>
                          <DiffContainer>
                            <ReactDiffViewer
                              oldValue={diff.oldValue}
                              newValue={diff.newValue}
                              showDiffOnly
                              compareMethod={DiffMethod.WORDS}
                              splitView={false}
                              hideLineNumbers
                              styles={DIFF_STYLES}
                            />
                          </DiffContainer>
                        </DiffField>
                      </EntryContent>
                    </DiffEntry>
                  ))}
                </DialectSection>
              )}
              {Object.entries(groupedByDialect).map(([dialect, entries]) => (
                <DialectSection key={dialect}>
                  <DialectLabel>{dialect}</DialectLabel>
                  {entries.map((entry) => (
                    <DiffEntry key={entry.translationDialectId}>
                      <EntryHeader>Phrasebook ID: {entry.phrasebookId}</EntryHeader>
                      <EntryContent>
                        {entry.textDiff && (
                          <DiffField>
                            <DiffContainer>
                              <ReactDiffViewer
                                oldValue={entry.textDiff.oldValue}
                                newValue={entry.textDiff.newValue}
                                showDiffOnly
                                compareMethod={DiffMethod.WORDS}
                                splitView={false}
                                hideLineNumbers
                                styles={DIFF_STYLES}
                              />
                            </DiffContainer>
                          </DiffField>
                        )}
                        {entry.statusDiff && (
                          <StatusDiff>
                            status: <OldStatus>{entry.statusDiff.oldValue}</OldStatus> {'->'}{' '}
                            <NewStatus>{entry.statusDiff.newValue}</NewStatus>
                          </StatusDiff>
                        )}
                      </EntryContent>
                    </DiffEntry>
                  ))}
                </DialectSection>
              ))}
            </DiffScrollContainer>
          )}
          {errors.length > 0 && (
            <ErrorSection>
              {errors.map((err, i) => (
                <div key={i}>{err}</div>
              ))}
            </ErrorSection>
          )}
          <EditorRowStyled>
            <Button
              variant="contained"
              size="small"
              disabled={loading || diffs.length === 0}
              onClick={handleSubmit}
            >
              {loading ? 'Importing...' : 'Submit'}
            </Button>
          </EditorRowStyled>
        </>
      )}

      {step === 'done' && <Section>{resultMessage}</Section>}
    </ModalWrapper>
  );
};

const InfoSection = styled.div`
  background-color: #f5f7fa;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 10px 14px;
  margin-bottom: 16px;
  font-size: 12px;
`;

const InfoTitle = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
  margin-top: 6px;
  &:first-child {
    margin-top: 0;
  }
`;

const InfoText = styled.div`
  color: #555;
  margin-bottom: 2px;
  code {
    background-color: #e8e8e8;
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 11px;
  }
`;

const Section = styled.div`
  margin-bottom: 16px;
`;

const ErrorSection = styled.div`
  color: red;
  margin-bottom: 8px;
  font-size: 12px;
`;

const EditorRowStyled = styled(EditorRow)`
  justify-content: flex-end;
  margin-top: 10px;
`;

const FileUploadStyled = styled(FileUpload)`
  margin-right: 5px;
`;

const FileName = styled.span`
  font-size: 12px;
  color: #555;
  margin-left: 8px;
`;

const DiffScrollContainer = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const DialectSection = styled.div`
  margin-bottom: 16px;
`;

const DialectLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  padding: 4px 8px;
  background-color: #e3f2fd;
  border-radius: 4px;
  margin-bottom: 8px;
  color: #1565c0;
`;

const DiffEntry = styled.div`
  margin-bottom: 8px;
  padding: 6px 8px;
  background-color: #fafafa;
  border-radius: 4px;
  border-left: 3px solid #e0e0e0;
`;

const EntryHeader = styled.div`
  font-size: 11px;
  color: #888;
  margin-bottom: 4px;
`;

const EntryContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const DiffField = styled.div`
  flex: 1;
`;

const DiffContainer = styled.div`
  background-color: #fff;
  padding: 4px 6px;
  border-radius: 2px;
`;

const StatusDiff = styled.div`
  font-size: 12px;
  white-space: nowrap;
  padding-top: 4px;
`;

const OldStatus = styled.span`
  color: #c62828;
  text-decoration: line-through;
`;

const NewStatus = styled.span`
  color: #2e7d32;
  font-weight: 600;
`;

export default MemoryPhrasebookImportModal;
